<template>
  <vue-js-modal
    v-if="formatter"
    :key="reload"
    id="agFormatterModal"
    class="agModal"
    name="agFormatterModal"
    draggable=".card-header"
    width="75%"
    height="85%"
    :shiftX=".5"
    :shiftY=".25"
    :resizable="true"
    :clickToClose="false">
    <b-card>
      <template #header>
        <h4 v-if="formatter._id" class="agModal-title">Edit Formatter {{formatter.shortname}}</h4>
        <h4 v-else class="agModal-title">New Formatter {{formatter.shortname}}</h4>
      </template>

      <div class="agInputGroup agInputGroupCentered">
        <label>Source:</label>
        <b-input
          type="text"
          :value="srcStr"
          autocomplete="false"
          disabled
        ></b-input>
      </div>
      <b-input-group size="md" prepend="Name">
        <b-input
          type="text"
          v-model="formatter.name"
          autocomplete="false"
          :disabled="disableUI || isLoading">
        </b-input>
      </b-input-group>
      <b-input-group size="md" prepend="Description">
        <b-input
          type="text"
          v-model="formatter.description"
          autocomplete="false"
          :disabled="disableUI || isLoading">
        </b-input>
      </b-input-group>
      <hr />
      <div class="agInputGroup agInputGroupCentered">
        <label>Format Type:</label>
        <b-form-select
          class="agSmallInput"
          v-model="formatter.subtype"
          :options="formatTypes"
          :disabled="disableUI || isLoading"
          @change="typeField_change">
        </b-form-select>
      </div>
      <div style="padding:4px;" class="agInputGroup agInputGroupCentered">
        <div class="agInputGroup agInputGroupInline">
          <label>In Field:</label>
          <b-form-select
            :key="'outFields-' + reloadFields"
            class="agLargeInput"
            v-model="formatter.inField"
            :disabled="disableUI || inFieldDisabled"
            @change="field_change($event, 'inField')">
            <optgroup v-for="(field, key) in fields" :key="key" :label="key">
              <option
                v-for="(option, index) in field"
                :key="index"
                :value="option.value">
                {{ option.text }}
              </option>
            </optgroup>
          </b-form-select>
        </div>
        <div class="agInputGroup agInputGroupInline">
          <label>Out Field:</label>
          <b-form-select
            :key="'outFields-' + reloadFields"
            class="agLargeInput"
            v-model="formatter.outField"
            :disabled="disableUI || isLoading"
            @change="field_change($event, 'outField')">
            <optgroup v-for="(field, key) in fields" :key="key" :label="key">
              <option
                v-for="(option, index) in field"
                :key="index"
                :value="option.value">
                {{ option.text }}
              </option>
            </optgroup>
          </b-form-select>
        </div>
      </div>
      <hr />

      <!-- Components based on type -->

      <ag-bigTextFormatter
        v-if="currentType == 'bigText'"
        ref="bigText"
        :disableUI="disableUI || isLoading"
        :formatter="formatter"
        :workflow="workflow">
      </ag-bigTextFormatter>

      <ag-castFormatter
        v-if="currentType == 'cast'"
        ref="cast"
        :disableUI="disableUI || isLoading"
        :formatter="formatter">
      </ag-castFormatter>

      <ag-dateFormatter
        v-if="currentType == 'date'"
        ref="date"
        :disableUI="disableUI || isLoading"
        :formatter="formatter">
      </ag-dateFormatter>

      <ag-geoFormatter
        v-if="currentType == 'geo'"
        ref="geo"
        :disableUI="disableUI || isLoading"
        :formatter="formatter"
        :workflow="workflow"
        :fields="fields">
      </ag-geoFormatter>

      <ag-mathFormatter
        v-if="currentType == 'math'"
        ref="math"
        :disableUI="disableUI || isLoading"
        :formatter="formatter"
        :workflow="workflow">
      </ag-mathFormatter>

      <ag-substringFormatter
        v-if="currentType == 'substring'"
        ref="substring"
        :disableUI="disableUI || isLoading"
        :formatter="formatter">
      </ag-substringFormatter>

      <ag-hashFormatter
        v-if="currentType == 'hash'"
        ref="hash"
        :disableUI="disableUI || isLoading"
        :formatter="formatter"
        :workflow="workflow">
      </ag-hashFormatter>

      <hr />
      <div class="agInputGroup agInputGroupCentered">
        <label>Destination:</label>
        <b-input type="text" :value="destName" class="agInput agExtraSmallInput" disabled></b-input>
      </div>

      <template #footer>
        <b-spinner v-if="isLoading" class="agModal-loadingSpinner"></b-spinner>
        <div v-else-if="modalMessage">
          <label class="agModal-message" :class="modalMessageClass">{{modalMessage}}</label>
        </div>

        <b-button class="agModal-buttonLeft" @click="cancelButton_click">Cancel</b-button>
        <b-button
          class="agModal-buttonRight"
          variant="primary"
          :disabled="disableUI || isLoading"
          @click="saveButton_click">
          Save
        </b-button>
        <b-button
          class="agModal-buttonRight"
          variant="danger"
          :disabled="disableUI || isLoading"
          @click="deleteButton_click">
          Delete
        </b-button>
      </template>
    </b-card>

    <!-- delete modal -->
    <b-modal
      class="agModal"
      ref="deleteModal"
      centered
      no-close-on-esc
      no-close-on-backdrop
      @keyup.enter.native="deleteButton_click">
      <template slot="modal-header">
        <h4 class="agModal-title danger">Delete Formatter</h4>
      </template>
      Are you sure you want to delete formatter
      <i>{{ name }}</i>?
      <template slot="modal-footer" slot-scope="{ cancel }">
        <b-button class="agModal-buttonLeft" @click="cancel">
          Cancel
        </b-button>
        <b-button
          class="agModal-buttonRight"
          variant="danger"
          @click="confirmDeleteButton_click">
          Delete
        </b-button>
      </template>
    </b-modal>

    </vue-js-modal>
</template>

<script>
// components
import BigTextFormatter from "nodes/formatters/BigTextFormatter";
import CastFormatter from "nodes/formatters/CastFormatter";
import DateFormatter from "nodes/formatters/DateFormatter";
import GeoFormatter from "nodes/formatters/GeoFormatter";
import MathFormatter from "nodes/formatters/MathFormatter";
import SubstringFormatter from "nodes/formatters/SubstringFormatter";
import HashFormatter from "nodes/formatters/HashFormatter";

// modules
import Vue from "vue";
import Server from "src/server";
import Util from "src/util/util";

export default {
  name: "ag-formatter-modal",

  components: {
    "ag-bigTextFormatter": BigTextFormatter,
    "ag-castFormatter": CastFormatter,
    "ag-dateFormatter": DateFormatter,
    "ag-geoFormatter": GeoFormatter,
    "ag-mathFormatter": MathFormatter,
    "ag-substringFormatter": SubstringFormatter,
    "ag-hashFormatter": HashFormatter
  },

  props: {
    node: Object,
    workflow: Object,
    disableUI: Boolean
  },

  data() {
    return {
      formatter: null,
      reload: 0,
      reloadFields: 0,
      isLoading: false,
      modalMessage: "",
      modalMessageClass: "",
      fields: {},
      currentType: "",
      formatTypes: [
        { value: "bigText", text: "Big Text" },
        { value: "cast", text: "Cast" },
        { value: "date", text: "Date" },
        { value: "geo", text: "Geometry" },
        { value: "math", text: "Math" },
        { value: "substring", text: "Substring" },
        { value: "lowercase", text: "To Lowercase" },
        { value: "uppercase", text: "To Uppercase" },
        { value: "trim", text: "Trim" },
        { value: "urlencode", text: "URL Encode" },
        { value: "hash", text: "Hash" }
      ]
    };
  },

  computed: {
    name() {
      if (this.formatter.name)
        return this.formatter.name;
      
      return this.formatter.shortname;
    },

    srcStr() {
      if (!this.formatter.src || this.formatter.src.length == 0) return "";

      var srcStr = "";
      for (var i = 0; i < this.formatter.src.length; i++) {
        var src = this.formatter.src[i];
        srcStr += src.shortname + ", ";
      }
      srcStr = srcStr.substring(0, srcStr.length - 2); // remove the last ,
      return srcStr;
    },

    destName() {
      if (this.formatter.dest) return this.formatter.dest.shortname;
      return "";
    },

    inFieldDisabled() {
      if (
        this.formatter.subtype == "bigText" ||
        this.formatter.subtype == "math" ||
        this.formatter.subtype == "geo" ||
        this.formatter.subtype == "hash"
      ) {
        this.formatter.inField = null;
        return true;
      }
      return false;
    }
  },

  watch: {},

  created() {},

  mounted() {},

  methods: {
    // UI Events

    cancelButton_click() {
      this.hide();
    },

    async saveButton_click() {
      this.modalMessage = "";

      // alert subcomponent
      var subcomponent = this.$refs[this.formatter.subtype];
      if (subcomponent) subcomponent.prepareForSave();

      var node = {
        object: this.formatter,
        type: "formatter"
      };
      this.isLoading = true;
      var result = await this.$store.dispatch("nodeStore/updateNode", {
        node: node
      });
      this.isLoading = false;
      if (result.data.success) {
        var updated = {
          type: "formatter",
          object: result.data.node
        };
        this.hide();
        this.$emit("updatedNode", updated);
      } else {
        this.modalMessage = result.data.error;
        this.modalMessageClass = "agModal-errorMessage";
      }
    },

    deleteButton_click() {
      this.$refs.deleteModal.show();
    },

    async confirmDeleteButton_click() {
      this.$refs.deleteModal.hide();
      var node = {
        object: this.formatter,
        type: "formatter"
      };
      this.isLoading = true;
      var result = await this.$store.dispatch("nodeStore/deleteNode", {
        node: node
      });
      this.$emit("deletedNode", node);
      this.isLoading = false;
      this.hide();
    },

    typeField_change() {
      this.formatter.config = {};
      this.currentType = this.formatter.subtype;
    },

    field_change(value, field) {
      if (value.field == "-- None --") this.formatter[field] = null;
    },

    // emitted Events

    // other

    async show() {
      await this.setFormatter();
      await Vue.nextTick();
      await this.reset();
      this.$modal.show("agFormatterModal");
    },

    hide() {
      this.$modal.hide("agFormatterModal");
    },

    async reset() {
      this.modalMessage = "";
      this.modalMessageClass = "";
      this.errorField = "";
      this.currentType = this.formatter.subtype;

      this.setFields();
      this.reloadFields++;

      await Vue.nextTick();
      let subcomponent = this.$refs[this.formatter.subtype];
      if (subcomponent) 
        subcomponent.reset();
    },

    async setFormatter() {
      if (this.node && this.node.type == "formatter")
        this.formatter = this.workflow.formatters.find(
          object => object._id == this.node.object._id
        );
      else this.formatter = null;
    },

    setFields() {
      var inputFields = Util.getInputFields(this.$store, this.workflow);
      this.fields = Util.fieldsToOptionGroups(inputFields);
    }
  }
};
</script>

<style scoped lang="css">
.vfm__content modal-content{
  max-height:70%;
  min-height:50%; 
  max-width: 84%; 
  min-width:50%; 
  margin-top:10%;
}

</style>



// WEBPACK FOOTER //
// src/components/nodes/formatters/FormatterModal.vue